/**
 * English translations
 */

import type { TranslationKeys } from './types';

export const en: TranslationKeys = {
  // Navigation
  nav: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    projects: 'Projects',
    experience: 'Experience',
    playground: 'Playground',
    contact: 'Contact',
    closeMenu: 'Close menu',
    openMenu: 'Open menu',
  },

  // Hero Section
  hero: {
    subtitle: 'CTO & Tech Lead\nAI Full Stack Developer\nXR Developer',
    tagline: 'Specialized in',
    yearsExperience: 'years of experience',
    contactMe: 'Contact Me',
    viewProjects: 'View Projects',
    downloadCV: 'Download CV',
    scroll: 'Scroll',
  },

  // About Section
  about: {
    greeting: 'Hello!',
    role: 'CTO & Tech Lead\nAI Full Stack Developer\nXR Developer',
    intro: {
      highlight:
        '<strong>AI Full-stack</strong> developer and <strong>CTO</strong> with over <span class="tech">9 years of experience</span> leading innovative technology projects.',
      specialization:
        'Specialized in <span class="tech">Unity3D</span>, <span class="tech">XR (AR/VR/MR)</span>, <span class="tech">3D configurators</span>, <span class="tech">AI</span>, and <span class="tech">React/TypeScript</span>.',
    },
    cards: {
      leadership: {
        title: 'Technical Leadership',
        content: `<strong>CTO at HausLook</strong>, I lead the development of platforms that combine <strong>full-stack web</strong> (React/TypeScript/Supabase), interactive <strong>3D configurators</strong> in Unity WebGL, and <strong>photorealistic rendering</strong> with 3ds Max and V-Ray. I integrate <em>AI strategically</em> to optimize processes and create intelligent agents. As <strong>Founder, CEO, and CTO of Lyceum VR</strong>, I have led <strong>technology consulting</strong> for international clients and managed <strong>multidisciplinary teams</strong> (technical and creative) on XR projects.`,
      },
      experience: {
        title: 'Diverse Experience',
        content: `My career spans from <strong>immersive XR experiences</strong> (AR/VR/MR) to complex <strong>software architecture</strong>. I have <strong>founded companies</strong> (Lyceum VR), <strong>led technical teams</strong>, and developed everything from <em>educational applications</em> to <em>military simulation software</em>. I'm passionate about <strong>R&D</strong>, staying up-to-date with the <strong>state of the art</strong> in cutting-edge technology, <em>researching and implementing</em> the latest innovations.`,
      },
      aiDevelopment: {
        title: 'AI-Assisted Development',
        content: `<strong>Pioneer</strong> in the use of AI-assisted development tools like <strong>Claude Code</strong> and <strong>Cursor</strong>. My web stack (<strong>React/TypeScript/Supabase</strong>) is <em>100% AI-Powered</em>, maximizing productivity and quality. In <strong>Unity/C#</strong> I combine <em>9+ years of traditional experience</em> with AI tools, leveraging the best of both worlds for highly complex XR projects.`,
      },
    },
    techStack: 'Tech Stack',
    techCategories: {
      unityXr: 'Unity & XR',
      unityXrBadge: '9+ years traditional + AI',
      fullStackWeb: 'Full Stack Web',
      fullStackBadge: 'AI-Powered',
      rendering3d: '3D & Rendering',
      aiTools: 'AI Tools',
      devTools: 'Tools & Version Control',
      devOps: 'DevOps & Cloud',
    },
  },

  // What I Do Section
  whatIDo: {
    title: 'Where technology meets creativity',
    subtitle:
      "I don't just create software, I design interactive experiences. My mission is to transform ideas into immersive digital solutions that are intuitive, engaging, and memorable.",
    services: {
      ctoLeadership: {
        title: 'CTO & Tech Leadership',
        description:
          'Technical leadership and strategic consulting for startups and tech companies.',
      },
      fullstack: {
        title: 'Full Stack Development',
        description:
          'Modern web applications with React, TypeScript, Supabase, and scalable architecture.',
      },
      configurators3d: {
        title: '3D Configurators',
        description:
          'Interactive WebGL configurators and photorealistic visualization solutions.',
      },
      xrDevelopment: {
        title: 'XR Development (AR/VR/MR)',
        description: 'Immersive extended reality experiences for multiple platforms.',
      },
      aiIntegration: {
        title: 'AI Integration',
        description:
          'Intelligent agents, AI automation, and AI-assisted development.',
      },
      seriousGames: {
        title: 'Serious Games',
        description: 'Video games with educational or business purposes.',
      },
      architecture: {
        title: 'Software Architecture',
        description: 'Design of scalable, maintainable, and high-performance systems.',
      },
      mobile: {
        title: 'iOS/Android',
        description: 'Native and cross-platform mobile application development.',
      },
    },
    scrollingBanner: {
      appDev: 'App Development',
      creativeDesign: 'Creative Design',
      softwareArch: 'Software Architecture',
      databases: 'Databases',
      videogames: 'Video Games',
    },
  },

  // Portfolio Section
  portfolio: {
    title: "Discover some of the projects I've worked on",
    viewAll: 'View all projects',
    viewDetails: 'View Details',
  },

  // Experience Section
  experience: {
    title: "I'm a developer passionate about the latest technology",
    myExperience: 'My Experience',
    myEducation: 'My Education',
    founder: 'Founder',
    partner: 'Partner',
    currently: 'Present',
  },

  // Contact Section
  contact: {
    title: 'Your next project starts here.',
    emailMe: 'Email Me',
    callMe: 'Call Me',
    question: 'Do you have a project in mind?',
    helpAs: 'I can help you as:',
    roles: {
      techLead: 'Tech Lead',
      cto: 'CTO',
      xrDev: 'XR Developer',
      aiDev: 'AI Developer',
      webDev: 'Web Developer',
      frontend: 'Frontend',
      backend: 'Backend',
    },
    letsTalk: "Let's Talk!",
  },

  // Playground Section
  playground: {
    title: 'Playground',
    subtitle: 'Interactive demos and real-time 3D experiments developed in Unity URP.',
    viewMore: 'View more interactive demos',
  },

  // Footer
  footer: {
    developedWith: 'Developed with',
    and: 'and',
  },

  // Projects Page
  projectsPage: {
    backToHome: '← Back to home',
    title: 'All my projects',
    subtitle:
      'Explore my complete portfolio of XR, video games, and software development projects.',
  },

  // Project Page
  projectPage: {
    backToProjects: '← Back to projects',
    platform: 'Platform',
    technology: 'Technology',
    client: 'Client',
    date: 'Date',
    location: 'Location',
    project: 'PROJECT',
    techUsed: 'Technologies used',
    projectInMind: 'Do you have a project in mind?',
    projectInMindSubtext:
      "Whether you need a Tech Lead, CTO, or developer (XR, AI, Web, Frontend or Backend), let's talk!",
    viewExternalProject: 'View external project',
  },

  // Playground Page
  playgroundPage: {
    backToHome: '← Back to home',
    title: 'Playground',
    subtitle: 'Interactive demos, 3D experiments, and prototypes.',
    moreInfo: 'More info',
    inDevelopment: 'In development',
    comingSoon: 'Coming Soon',
    nextExperiment: 'Next experiment',
    viewers: {
      renderCreator: {
        title: 'Render Creator - Interior Configurator',
        description:
          'Real-time bathroom configurator developed in Unity URP with advanced optimizations.',
      },
      houseNavigator: {
        title: '3D House Navigator',
        description:
          'First-person virtual tour developed in Unity URP with baked GI lighting.',
      },
      model3d: {
        title: 'Interactive 3D Viewer',
        description: '3D models with animations and PBR materials. Supports GLB, GLTF, FBX.',
      },
      comingSoon: {
        title: 'Coming Soon',
        description: 'New experiments and demos in development. Stay tuned.',
      },
    },
    modal: {
      features: 'Features',
      optimization: 'Technical Optimization',
      techStack: 'Tech Stack',
    },
    bathrooms: {
      bath1: 'Bathroom 1',
      bath2: 'Bathroom 2',
      bath3: 'Bathroom 3',
      bath4: 'Bathroom 4',
    },
  },

  // Common
  common: {
    loading: 'Loading...',
    error: 'Error',
    close: 'Close',
  },
};
